<script setup lang="ts">
import { SancareMultiselect, SancareOcticon } from '@sancare/typescript-commons'
import SancareToggle from '@sancare/typescript-commons/src/components/SancareToggle.vue'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import VueSlider from 'vue-slider-component'
import { useStore } from 'vuex'

import SancareSettingData from '../settings/sancareSettingsData'
import SettingData from '../settings/settingData'
import AppFinessInput from './AppFinessInput.vue'

export interface AppSettingsManagerProps {
  sancareOnly: boolean
}

const store = useStore()
const props = withDefaults(
  defineProps<AppSettingsManagerProps>(),
  {
    sancareOnly: false
  }
)


const sliderSettings = reactive({})
const boolSettings = reactive({})
// holds the local selection for array settings ('finess': FinessSettingItem[], 'medicalUnit': number[])
const arraySettingValues = reactive({})

const hasEdition = ref(false)
const error = ref<string | null>(null)
const success = ref<boolean>(false)
const showWarning = ref<boolean>(false)
const warningMessage = ref<string>('')

const aiFlagEnabled = computed(() => store.getters.aiFlagEnabled)
const medicalUnitsTree = computed(() => store.state.settings.medicalUnits)
const medicalUnitsRequest = computed(() => store.state.remoteLists.medicalUnitsRequest)
const setAppSettingsAction = computed(() => props.sancareOnly
  ? 'setSancareSettings'
  : 'setAppSettings'
)
const settingData = computed(() => props.sancareOnly
  ? SancareSettingData
  : SettingData
)
const appSettings = computed(() => props.sancareOnly
  ? store.state.settings.sancareSettings
  : store.state.settings.appSettings
)
const setAppSettingsRequest = computed(() => props.sancareOnly
  ? store.state.settings.setSancareSettingsRequest
  : store.state.settings.setAppSettingsRequest
)
const displayableSettings = computed(() => {
  const result = {}
  Object.keys(settingData.value).forEach((mode) => {
    result[mode] = {
      label: settingData.value[mode].label,
      settings: settingData.value[mode].settings.filter((setting) => {
        if (setting.name !== 'ai_confidence_external_flag_threshold') {
          return true
        }
        return aiFlagEnabled.value
      })
    }
  })
  return result
})

watch(setAppSettingsRequest, (req) => {
  if (req.ok) {
    hasEdition.value = false
    success.value = true
    error.value = null
    showWarning.value = false
    warningMessage.value = ''
  } else if (req.error) {
    error.value = req.error
  }
})

onMounted(() => updateLocaleSettings(appSettings.value))

function updateLocaleSettings(appSettings) {

  Object.keys(settingData.value).forEach((category) => {
    settingData.value[category].settings.forEach((setting) => {
      const settingName = setting.name
      switch (setting.type) {
        case 'bool':
          boolSettings[settingName] = appSettings.boolSettings[settingName].value !== null
            ? appSettings.boolSettings[settingName].value
            : appSettings.boolSettings[settingName].default
          break
        case 'slider':
          sliderSettings[settingName] = appSettings.sliderSettings[settingName].index !== null
            ? appSettings.sliderSettings[settingName].index
            : appSettings.sliderSettings[settingName].default
          break
        case 'finess':
          arraySettingValues[settingName] = [
            ...appSettings.arraySettings[settingName]
          ]
          break
        case 'medicalUnit':
          arraySettingValues[settingName] = appSettings.arraySettings[settingName].map((medicalUnit) => medicalUnit.id)
          break
      }
    })
  })
}

function getMarks(settingName: string, value: number) {
  if (settingName === 'standard_mode_dp_financial_threshold') {
    const options = [10, 30, 50, 100, 300, 500, 1000]
    return { label: `${options[value]}€` }
  } else if (settingName.endsWith('_financial_risk')) {
    if (value === 0) {
      return { label: 'Faible' }
    } else if (value === 11) {
      return { label: 'Élevé' }
    }
  } else if (settingName === 'automation_mode_dr_min_confidence') {
    switch (value) {
      case 0:
        return { label: '0%' }
      case 1:
        return { label: '5%' }
      case 2:
        return { label: '20%' }
      case 3:
        return { label: '40%' }
      case 4:
        return { label: '60%' }
      case 5:
        return { label: '80%' }
      case 6:
        return { label: '95%' }
      case 7:
        return { label: '100%' }
      default:
        return { label: '' }
    }
  } else if (settingName === 'ai_confidence_external_flag_threshold') {
    switch (value) {
      case 0:
        return { label: '60%' }
      case 2:
        return { label: '70%' }
      case 4:
        return { label: '80%' }
      case 6:
        return { label: '90%' }
      default:
        return { label: '' }
    }
  } else if (settingName === 'standard_mode_dp_threshold') {
    switch (value) {
      case 0:
        return { label: '10%' }
      case 2:
        return { label: '30%' }
      case 4:
        return { label: '50%' }
      case 6:
        return { label: '70%' }
      case 8:
        return { label: '90%' }
      default:
        return { label: '' }
    }
  } else if (settingName === 'new_coding_das_threshold') {
    return { label: `${value * 10}%` }
  } else {
    if (value === 0) {
      return { label: 'Quantité' }
    } else if (value === 1) {
      return { label: 'Équilibré' }
    } else {
      return { label: 'Qualité' }
    }
  }
  return { label: '' }
}

function onEdit(settingName: string) {
  hasEdition.value = true
  error.value = null
  success.value = false

  if (settingData.value.automation?.settings?.find((elem) => elem.name === settingName)) {
    warningMessage.value = 'La modification des paramètres d\'automatisation entraîne la désactivation de tous les groupes d\'automatisation'
    showWarning.value = true
  }
}

function clearEdition() {
  updateLocaleSettings(appSettings.value)
  hasEdition.value = false
}

function saveEdition() {
  const editedSettings = {
    sliderSettings: [],
    boolSettings: [],
    arraySettings: []
  }

  Object.keys(appSettings.value.sliderSettings).forEach((name) => {
    const setting = appSettings.value.sliderSettings[name]
    const settingValue = setting.index === null ? setting.default : setting.index
    if (sliderSettings[name] !== settingValue) {
      editedSettings.sliderSettings.push({ name, index: sliderSettings[name] })
    }
  })

  Object.keys(appSettings.value.boolSettings).forEach((name) => {
    const setting = appSettings.value.boolSettings[name]
    const settingValue = setting.value === null ? setting.default : setting.value
    if (boolSettings[name] !== settingValue) {
      editedSettings.boolSettings.push({ name, value: boolSettings[name] })
    }
  })

  Object.keys(settingData.value).forEach((category) => {
    settingData.value[category].settings.forEach((setting) => {
      if (setting.type === 'finess') {
        editedSettings.arraySettings.push({ name: setting.name, value: arraySettingValues[setting.name].map((elm) => elm.finess) })
      } else if (setting.type === 'medicalUnit') {
        editedSettings.arraySettings.push({ name: setting.name, value: arraySettingValues[setting.name].map(String) })
      }
    })
  })

  error.value = null
  warningMessage.value = ''
  showWarning.value = false
  store.dispatch(setAppSettingsAction.value, editedSettings).then(() => {
    updateLocaleSettings(appSettings.value)
  })
}

function removeAppFiness(finess, settingName) {
  arraySettingValues[settingName] = arraySettingValues[settingName].filter((elm) => elm.finess !== finess)
  onEdit(settingName)
}

function addAppFiness({ finess, shortDescription, settingName }) {
  if (!arraySettingValues[settingName].find((elm) => elm.finess === finess)) {
    arraySettingValues[settingName].push({ finess, shortDescription })
    onEdit(settingName)
  }
}

function updateMedicalUnits(settingName, selectedIds) {
  arraySettingValues[settingName] = selectedIds
  onEdit(settingName)
}
</script>

<template>
  <div>
    <div class="settings-table">
      <div
        v-for="(modeSetting, mode) in displayableSettings"
        :key="mode"
      >
        <div class="settings-table__header-row row">
          <div class="col-auto settings-table row mx-3">
            <h2>{{ sancareOnly ? $t(modeSetting.label) : modeSetting.label }}</h2>
            <small
              v-if="showWarning && mode === 'automation'"
              class="col text-end"
            >
              <sancare-octicon
                name="alert"
                :width="18"
                :height="18"
              />
              {{ warningMessage }}
            </small>
          </div>
        </div>
        <div
          v-for="setting in modeSetting.settings"
          :key="setting.name"
          class="settings-table__data-row row"
        >
          <div class="col-7 settings-table__data-cell">
            {{ sancareOnly ? $t(setting.label) : setting.label }}
            <div
              v-if="setting.type === 'finess'"
            >
              <div
                v-for="finess in arraySettingValues[setting.name]"
                :key="finess['finess']"
                class="text-bg-primary badge col-auto mx-1 mb-1 ps-2 pe-2"
              >
                <span>{{ finess['shortDescription'] }} ({{ finess['finess'] }})</span>
                <span
                  class="criteria-remove"
                  @click="removeAppFiness(finess['finess'], setting.name)"
                >
                  <sancare-octicon
                    name="x"
                    :width="14"
                    :height="11"
                  />
                </span>
              </div>
            </div>
          </div>
          <div class="col settings-table__data-cell text-end">
            <div v-if="setting.type === 'slider' && appSettings.sliderSettings[setting.name]">
              <vue-slider
                v-model="sliderSettings[setting.name]"
                :name="setting.name"
                :process="false"
                :v-data="Array.from(appSettings.sliderSettings[setting.name].values.keys())"
                :marks="(value) => getMarks(setting.name, value)"
                :tooltip="'none'"
                class="me-3 mb-3"
                @change="onEdit(setting.name)"
              />
            </div>
            <div v-else-if="setting.type === 'bool'">
              <sancare-toggle
                v-model:value="boolSettings[setting.name]"
                class="col-auto justify-content-end"
                :name="setting.name"
                @toggle-change="onEdit(setting.name)"
              />
            </div>
            <div v-else-if="setting.type === 'finess' && arraySettingValues[setting.name]">
              <app-finess-input
                :setting-name="setting.name"
                @select-finess="addAppFiness"
              />
            </div>
            <div v-else-if="setting.type === 'medicalUnit' && arraySettingValues[setting.name]">
              <sancare-multiselect
                :title="$t('global.medicalUnits')"
                :selected-items="arraySettingValues[setting.name]"
                :items="medicalUnitsTree"
                :is-ready="!medicalUnitsRequest.fetching"
                @update-selection="(ids) => updateMedicalUnits(setting.name, ids)"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="settings-table__data-row row align-items-center">
        <div class="col">
          <div
            v-if="error"
            class="text-danger"
          >
            {{ error }}
          </div>
          <div
            v-if="setAppSettingsRequest.fetching"
            class="row"
          >
            <div class="loader loader-sm mt-0 mx-2" />
            <span
              v-if="showWarning"
            >
              Calcul des automatisations en cours...
            </span>
          </div>
          <div
            v-else-if="success"
            class="text-success"
          >
            Les modifications ont bien été enregistrées
          </div>
        </div>
        <div class="col-auto">
          <button
            :disabled="!hasEdition || setAppSettingsRequest.fetching"
            name="clearForm"
            class="btn btn-push btn-primary btn-padded"
            @click="clearEdition()"
          >
            Annuler les modifications
          </button>
        </div>
        <div class="col-auto text-end">
          <button
            :disabled="!hasEdition || setAppSettingsRequest.fetching"
            name="submitForm"
            class="btn btn-push btn-primary btn-padded"
            @click="saveEdition()"
          >
            Enregistrer les modifications
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
